'use client'

type IconName =
  | 'home' | 'receipt' | 'chart' | 'target' | 'wallet' | 'bell' | 'search'
  | 'plus' | 'arrow_up' | 'arrow_down' | 'arrow_right' | 'chevron_right'
  | 'chevron_down' | 'check' | 'x' | 'sparkle' | 'user' | 'users'
  | 'settings' | 'bank' | 'cart' | 'car' | 'fork' | 'bolt' | 'baby'
  | 'heart' | 'gift' | 'lock' | 'shield' | 'camera' | 'upload' | 'filter'
  | 'sort' | 'ellipsis' | 'edit' | 'trash' | 'flame' | 'download'
  | 'calendar' | 'pie' | 'list' | 'eye' | 'sliders' | 'link' | 'play'
  | 'info' | 'log_out' | 'refresh' | 'chevron_left'

interface IconProps {
  name: IconName
  size?: number
  color?: string
  strokeWidth?: number
  className?: string
}

export default function Icon({ name, size = 18, color = 'currentColor', strokeWidth = 1.75, className }: IconProps) {
  const paths: Record<IconName, React.ReactNode> = {
    home: <><path d="M3 10.5L12 3l9 7.5V20a1 1 0 01-1 1h-5v-7h-6v7H4a1 1 0 01-1-1v-9.5z"/></>,
    receipt: <><path d="M5 3h14v18l-3-2-3 2-3-2-3 2-2-1.3V3z"/><path d="M8 8h8M8 12h8M8 16h5"/></>,
    chart: <><path d="M3 3v18h18"/><path d="M7 14l4-4 3 3 5-6"/></>,
    target: <><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5" fill={color}/></>,
    wallet: <><path d="M3 7a2 2 0 012-2h13v4M3 7v11a2 2 0 002 2h15a1 1 0 001-1V9a1 1 0 00-1-1H5a2 2 0 01-2-1z"/><circle cx="17" cy="14" r="1.3" fill={color}/></>,
    bell: <><path d="M6 8a6 6 0 0112 0c0 7 3 7 3 9H3c0-2 3-2 3-9z"/><path d="M10 21a2 2 0 004 0"/></>,
    search: <><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.5-4.5"/></>,
    plus: <><path d="M12 5v14M5 12h14"/></>,
    arrow_up: <><path d="M12 19V5M5 12l7-7 7 7"/></>,
    arrow_down: <><path d="M12 5v14M5 12l7 7 7-7"/></>,
    arrow_right: <><path d="M5 12h14M13 5l7 7-7 7"/></>,
    chevron_right: <><path d="M9 6l6 6-6 6"/></>,
    chevron_down: <><path d="M6 9l6 6 6-6"/></>,
    check: <><path d="M5 12l5 5 9-11"/></>,
    x: <><path d="M6 6l12 12M6 18L18 6"/></>,
    sparkle: <><path d="M12 3v6m0 6v6M3 12h6m6 0h6M5.5 5.5l4 4m5 5l4 4M5.5 18.5l4-4m5-5l4-4"/></>,
    user: <><circle cx="12" cy="8" r="4"/><path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8"/></>,
    users: <><circle cx="9" cy="8" r="3.5"/><path d="M2 21c0-3.9 3.1-7 7-7s7 3.1 7 7"/><circle cx="17" cy="6" r="2.5"/><path d="M16 14c2.8 0 5 2.2 5 5"/></>,
    settings: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z"/></>,
    bank: <><path d="M3 10l9-6 9 6"/><path d="M5 10v9M9 10v9M15 10v9M19 10v9M3 21h18"/></>,
    cart: <><circle cx="9" cy="20" r="1.5"/><circle cx="17" cy="20" r="1.5"/><path d="M3 4h2l2.5 11h11l2-7H6"/></>,
    car: <><path d="M3 13l2-5h14l2 5v5h-3v-2H6v2H3v-5z"/><circle cx="7" cy="16" r="1.3"/><circle cx="17" cy="16" r="1.3"/></>,
    fork: <><path d="M7 3v8a3 3 0 003 3v7M7 7v0M17 3l-1 5a2 2 0 002 2v11"/></>,
    bolt: <><path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z"/></>,
    baby: <><circle cx="12" cy="9" r="4"/><circle cx="10" cy="8" r=".5" fill={color}/><circle cx="14" cy="8" r=".5" fill={color}/><path d="M5 21c0-4 3-7 7-7s7 3 7 7"/></>,
    heart: <><path d="M12 21s-7-4.5-9-10a5 5 0 019-3 5 5 0 019 3c-2 5.5-9 10-9 10z"/></>,
    gift: <><rect x="3" y="8" width="18" height="13" rx="1"/><path d="M3 12h18M12 8v13M8 8a3 3 0 010-6c2 0 4 6 4 6S10 8 8 8zM16 8a3 3 0 000-6c-2 0-4 6-4 6s2 0 4 0z"/></>,
    lock: <><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 018 0v4"/></>,
    shield: <><path d="M12 3l8 3v6c0 4-3 8-8 9-5-1-8-5-8-9V6l8-3z"/></>,
    camera: <><rect x="3" y="6" width="18" height="14" rx="2"/><circle cx="12" cy="13" r="4"/><path d="M8 6l2-3h4l2 3"/></>,
    upload: <><path d="M12 16V4M6 10l6-6 6 6M4 20h16"/></>,
    filter: <><path d="M3 5h18M6 12h12M10 19h4"/></>,
    sort: <><path d="M7 4v16M4 8l3-4 3 4M17 4v16M14 16l3 4 3-4"/></>,
    ellipsis: <><circle cx="5" cy="12" r="1.5" fill={color} stroke="none"/><circle cx="12" cy="12" r="1.5" fill={color} stroke="none"/><circle cx="19" cy="12" r="1.5" fill={color} stroke="none"/></>,
    edit: <><path d="M16 3l5 5L8 21H3v-5L16 3z"/></>,
    trash: <><path d="M3 6h18M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2M5 6l1 14a2 2 0 002 2h8a2 2 0 002-2l1-14"/></>,
    flame: <><path d="M12 22c4 0 7-3 7-7 0-3-2-5-3-6-1 1-1 3-2 3s-2-3-1-7c-3 1-7 5-7 10 0 4 3 7 6 7z"/></>,
    download: <><path d="M12 4v12M6 10l6 6 6-6M4 20h16"/></>,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></>,
    pie: <><path d="M12 3v9h9a9 9 0 11-9-9z"/><path d="M21 9a9 9 0 00-6-6v6h6z"/></>,
    list: <><path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01"/></>,
    eye: <><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></>,
    sliders: <><path d="M4 6h6M14 6h6M4 12h2M10 12h10M4 18h12M20 18h0"/><circle cx="12" cy="6" r="2"/><circle cx="8" cy="12" r="2"/><circle cx="18" cy="18" r="2"/></>,
    link: <><path d="M10 14a5 5 0 007 0l3-3a5 5 0 00-7-7l-1 1"/><path d="M14 10a5 5 0 00-7 0l-3 3a5 5 0 007 7l1-1"/></>,
    play: <><path d="M6 4l14 8-14 8V4z" fill={color}/></>,
    info: <><circle cx="12" cy="12" r="9"/><path d="M12 8h.01M11 12h1v5h1"/></>,
    log_out: <><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></>,
    refresh: <><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></>,
    chevron_left: <><path d="M15 6l-6 6 6 6"/></>,
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={{ display: 'block', flexShrink: 0 }}
    >
      {paths[name] ?? null}
    </svg>
  )
}
